import { useState } from "react";
import { AlertTriangle, ChevronRight, RefreshCw } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

/** Detailseiten: Hauptinhalt ca. 2/3, Seitenspalte ca. 1/3, mobil einspaltig. */
export function DetailLayout({
  main,
  aside,
}: {
  main: React.ReactNode;
  aside: React.ReactNode;
}) {
  return (
    <div className="grid items-start gap-4 lg:grid-cols-3">
      <div className="min-w-0 space-y-4 lg:col-span-2">{main}</div>
      <div className="min-w-0 space-y-4">{aside}</div>
    </div>
  );
}

/**
 * Eine Formular- oder Inhaltsgruppe als Karte: kurzer Titel, optional eine
 * Zeile Beschreibung, rechts Aktionen, unten optional eine Fußzeile.
 */
export function FormSection({
  title,
  description,
  actions,
  footer,
  children,
  size,
  className,
  contentClassName,
}: {
  title: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  footer?: React.ReactNode;
  children?: React.ReactNode;
  size?: "default" | "sm";
  className?: string;
  contentClassName?: string;
}) {
  return (
    <Card size={size} className={className}>
      {/* Mobil stehen die Aktionen unter dem Titel statt daneben. */}
      <CardHeader className="max-sm:has-data-[slot=card-action]:grid-cols-1">
        <CardTitle>{title}</CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
        {actions && (
          <CardAction className="flex flex-wrap gap-1.5 max-sm:col-start-1 max-sm:row-span-1 max-sm:row-start-auto max-sm:justify-self-start">
            {actions}
          </CardAction>
        )}
      </CardHeader>
      {children && (
        <CardContent className={cn("space-y-4", contentClassName)}>
          {children}
        </CardContent>
      )}
      {footer && (
        <CardFooter className="justify-end gap-2 bg-card">{footer}</CardFooter>
      )}
    </Card>
  );
}

/** Progressive Disclosure: selten gebrauchte Felder hinter „Erweitert“. */
export function AdvancedSection({
  label = "Erweitert",
  defaultOpen = false,
  children,
  className,
}: {
  label?: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
  className?: string;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Collapsible open={open} onOpenChange={setOpen} className={className}>
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-1 rounded-md text-sm font-medium text-muted-foreground outline-none hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50"
        >
          <ChevronRight
            className={cn("size-4 transition-transform", open && "rotate-90")}
            aria-hidden
          />
          {label}
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-3">{children}</CollapsibleContent>
    </Collapsible>
  );
}

/** Kennzahl-Kachel für Analytics-Seiten. */
export function StatTile({
  label,
  value,
  hint,
  loading,
}: {
  label: string;
  value: React.ReactNode;
  hint?: React.ReactNode;
  loading?: boolean;
}) {
  return (
    <Card size="sm" className="gap-1">
      <CardContent className="space-y-1">
        <div className="text-xs font-medium text-muted-foreground">{label}</div>
        {loading ? (
          <Skeleton className="h-7 w-20" />
        ) : (
          <div className="text-2xl font-semibold tracking-tight tabular-nums">
            {value}
          </div>
        )}
        {hint && !loading && (
          <div className="text-xs text-muted-foreground">{hint}</div>
        )}
      </CardContent>
    </Card>
  );
}

/** Verständlicher Fehlerzustand mit „Erneut versuchen“. */
export function ErrorState({
  title = "Laden fehlgeschlagen",
  message,
  onRetry,
  className,
}: {
  title?: string;
  message: string;
  onRetry?: () => void;
  className?: string;
}) {
  return (
    <Alert variant="critical" className={className}>
      <AlertTriangle />
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="break-words">{message}</span>
        {onRetry && (
          <Button size="xs" variant="secondary" onClick={onRetry}>
            <RefreshCw />
            Erneut versuchen
          </Button>
        )}
      </AlertDescription>
    </Alert>
  );
}

/** Platzhalter beim ersten Laden einer Seite. */
export function PageSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div aria-busy="true" aria-label="Lädt" className="space-y-4">
      <Skeleton className="h-7 w-56" />
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} className="h-40 rounded-xl" />
      ))}
    </div>
  );
}
